import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { loadPrevisFile } from '@/previs/previsLoader.ts';
import { isZipArchive, loadArchive } from '@/previs/archiveLoader.ts';
import { useProjectStore } from '@/store/projectStore.ts';
import { useAccountStore } from '@/store/accountStore.ts';
import { loadProject } from '@/store/actions.ts';
import { AccountButton } from '@/components/AccountButton.tsx';
import { AuthDialog } from '@/components/AuthDialog.tsx';
import { TokenPurchaseDialog } from '@/components/TokenPurchaseDialog.tsx';
import styles from './FileLoader.module.css';

const DEV_FIXTURE_URL = '/dev-fixtures/sample.previs';

interface FileLoaderProps {
  sampleUrl?: string;
}

export function FileLoader({ sampleUrl }: FileLoaderProps = {}) {
  const { t } = useTranslation('fileLoader');
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const setOriginalFile = useProjectStore(s => s.setOriginalFile);
  const authDialogOpen = useAccountStore(s => s.authDialogOpen);
  const setAuthDialogOpen = useAccountStore(s => s.setAuthDialogOpen);
  const tokenPurchaseDialogOpen = useAccountStore(s => s.tokenPurchaseDialogOpen);
  const setTokenPurchaseDialogOpen = useAccountStore(s => s.setTokenPurchaseDialogOpen);

  // In dev mode, auto-load the fixture project so debug/localize pages work without drag-drop
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    let cancelled = false;
    setIsLoading(true);
    fetch(DEV_FIXTURE_URL)
      .then(r => { if (!r.ok) throw new Error(`${r.status}`); return r.arrayBuffer(); })
      .then(async buffer => {
        if (cancelled) return;
        const project = isZipArchive(buffer)
          ? await loadArchive(buffer)
          : await loadPrevisFile(buffer);
        setOriginalFile(new Uint8Array(buffer), 'sample.previs');
        await loadProject(project);
      })
      .catch(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [setOriginalFile]);

  // Auto-fetch sample project from URL (landing page "Try Sample" flow)
  useEffect(() => {
    if (!sampleUrl) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    (async () => {
      const response = await fetch(sampleUrl);
      if (!response.ok) throw new Error(`Download failed (${response.status})`);
      const buffer = await response.arrayBuffer();
      if (cancelled) return;
      const project = isZipArchive(buffer)
        ? await loadArchive(buffer)
        : await loadPrevisFile(buffer);
      setOriginalFile(new Uint8Array(buffer), 'Sample Project.previs');
      // Open in read-only mode for sample projects
      useProjectStore.getState().setReadOnly(true);
      await loadProject(project);
    })().catch(err => {
      if (!cancelled) {
        console.error('Failed to load sample project:', err);
        setError(err instanceof Error ? err.message : 'Failed to load sample');
        setIsLoading(false);
      }
    });

    return () => { cancelled = true; };
  }, [sampleUrl, setOriginalFile]);

  const MAX_FILE_SIZE_MB = 100;

  const handleFile = useCallback(async (file: File) => {
    if (!file.name.endsWith('.previs')) {
      setError(t('dropFileError'));
      return;
    }

    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > MAX_FILE_SIZE_MB) {
      const proceed = window.confirm(
        t('largeFileConfirm', { sizeMB: sizeMB.toFixed(0) })
      );
      if (!proceed) return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const buffer = await file.arrayBuffer();
      let project;
      if (isZipArchive(buffer)) {
        // Archive: zip containing .previs + thumbnail/video assets
        project = await loadArchive(buffer);
      } else {
        project = await loadPrevisFile(buffer);
      }
      setOriginalFile(new Uint8Array(buffer), file.name);
      setStatusMsg('Loading audio...');
      await loadProject(project);
    } catch (e) {
      console.error('Failed to load .previs file:', e);
      setError(t('parseErrorPrefix', { errorMessage: e instanceof Error ? e.message : 'Unknown error' }));
    } finally {
      setIsLoading(false);
    }
  }, [setOriginalFile, t]);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const onDragLeave = useCallback(() => setIsDragging(false), []);

  const onClick = useCallback(() => inputRef.current?.click(), []);

  const onFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  return (
    <div className={styles.overlay}>
      <div className={styles.accountCorner}>
        <AccountButton dropdownDirection="below" />
      </div>
      <div
        className={`${styles.dropZone} ${isDragging ? styles.dropZoneActive : ''}`}
        data-file-drop-target=""
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
      >
        {isLoading ? (
          <>
            <div className={styles.spinner} />
            <div className={styles.subtitle}>{statusMsg ?? t('loadingProject')}</div>
          </>
        ) : (
          <>
            <div className={styles.icon}>🎬</div>
            <div className={styles.title}>{t('dropZoneTitle')}</div>
            <div className={styles.subtitle}>{t('dropZoneOr')}</div>
            <button className={styles.browseBtn} onClick={onClick}>
              {t('loadPrevisButton')}
            </button>
            {error && <div className={styles.error}>{error}</div>}
          </>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept=".previs"
        style={{ display: 'none' }}
        onChange={onFileChange}
      />
      {authDialogOpen && <AuthDialog open onClose={() => setAuthDialogOpen(false)} />}
      {tokenPurchaseDialogOpen && <TokenPurchaseDialog open onClose={() => setTokenPurchaseDialogOpen(false)} />}
    </div>
  );
}
